/**
 * Copyright (c) SAGE3 Development Team 2026. All Rights Reserved
 * University of Hawaii, University of Illinois Chicago, Virginia Tech
 *
 * Distributed under the terms of the SAGE3 License.  The full license is in
 * the file LICENSE, distributed as part of this software.
 *
 * Modified by Pak Kwan 2026-07-27: Added NarrativeLinkerMode with 'N' key shortcut for testing
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Box, useColorModeValue, useToast } from '@chakra-ui/react';

import {
  useUIStore,
  useAbility,
  WheelStepZoom,
  MinZoom,
  MaxZoom,
  useUserSettings,
  useHotkeys,
  useLinkStore,
  useUser,
  useThrottleApps,
  usePresenceStore,
  getFrameViewport,
  getFrameById,
  useCursorBoardPosition,
  useAppStore,
} from '@sage3/frontend';
import { Background, Apps, Whiteboard, Lasso, PresenceComponent, Links, CursorArrow, LinkerMode, NarrativeLinkerMode } from './components';
import { CreateSlideDeckDialog } from '../../../../components/CreateSlideDeckDialog';
import { SidebarAnchors } from '../ui/components';

type BackgroundLayerProps = {
  boardId: string;
  roomId: string;
};

export function BackgroundLayer(props: BackgroundLayerProps) {
  // Abilities
  const canLasso = useAbility('lasso', 'apps');

  // Settings
  const { settings } = useUserSettings();
  const primaryActionMode = settings.primaryActionMode;

  // UI store
  const scale = useUIStore((state) => state.scale);
  const boardWidth = useUIStore((state) => state.boardWidth);
  const boardHeight = useUIStore((state) => state.boardHeight);
  const selectedApp = useUIStore((state) => state.selectedAppId);
  const setSelectedApp = useUIStore((state) => state.setSelectedApp);
  const setBoardPosition = useUIStore((state) => state.setBoardPosition);
  const boardPosition = useUIStore((state) => state.boardPosition);
  const boardLocked = useUIStore((state) => state.boardLocked);
  const setBoardSynced = useUIStore((state) => state.setBoardSynced);
  const setScale = useUIStore((state) => state.setScale);
  const narrativeLinkerActive = useUIStore((state) => state.narrativeLinkerActive);
  const setNarrativeLinkerActive = useUIStore((state) => state.setNarrativeLinkerActive);

  // Local States with Delayed Syncing to useUIStore
  const [localBoardPosition, setLocalBoardPosition] = useState({ x: boardPosition.x, y: boardPosition.y, scale: scale });
  const [localSynced, setLocalSynced] = useState(true); // optimize performance against the useUIStore
  const [, setLastTouch] = useState([
    { x: 0, y: 0 },
    { x: 0, y: 0 },
  ]);
  const [, setCursorPos] = useState({ x: 0, y: 0 });

  // Clear linked app ID when exiting narrative linker mode
  const clearLinkedAppId = useLinkStore((s) => s.cacheLinkedAppId);

  // Split-screen state (Phase 4: Three-section split)
  const { user } = useUser();
  const apps = useThrottleApps(250);
  const splitConfig = useUIStore((state) => state.splitConfig);
  const presences = usePresenceStore((state) => state.presences);
  const following = usePresenceStore((state) => state.following);

  // Phase 4: Quick Frame Creation hooks
  const toast = useToast();
  const { getBoardCursor } = useCursorBoardPosition();
  const createApp = useAppStore((state) => state.create);
  const addLink = useLinkStore((state) => state.addLink);

  // Phase 4: Create Slide Deck dialog state
  const [showSlideDeckDialog, setShowSlideDeckDialog] = useState(false);
  const [slideDeckStartPosition, setSlideDeckStartPosition] = useState({ x: 0, y: 0 });

  // Check if split mode should be active (wall client + split enabled + both frames selected)
  const isWallClient = user?.data.userType === 'wall';
  const shouldSplit = isWallClient && splitConfig.enabled && splitConfig.leftFrameId && splitConfig.rightFrameId;

  // Color mode values for split sections
  const sectionBg = useColorModeValue('#f7fafc', '#1a202c');
  const middleBg = useColorModeValue('#ffffff', '#171923');
  const dividerColor = useColorModeValue('#3182ce', '#4299e1');

  // Used to differentiate between board drag and app deselect
  const [, setStartedDragOn] = useState<'board' | 'board-actions' | 'app' | 'app-resize' | 'other'>('other');

  // The fabled isMac const
  const isMac = useMemo(() => /(Mac|iPhone|iPod|iPad)/i.test(navigator.userAgent), []);

  const movementTimeoutRef = useRef<number | null>(null);
  const movementZoomSafetyTimeoutRef = useRef<number | null>(null);

  // Touch pinch-zoom tuning: small distance changes between fingers should not
  // cause large zoom jumps. Use a deadzone and clamp the per-frame scale ratio.
  const TOUCH_PINCH_DEADZONE = 10;    // pixels of distance change before zoom engages
  const TOUCH_PINCH_MIN_RATIO = 0.95; // max zoom-out per frame (5%)
  const TOUCH_PINCH_MAX_RATIO = 1.05; // max zoom-in per frame (5%)

  // Temporary keyboard shortcut for NarrativeLinkerMode testing (Phase 1A.3)
  // Press 'N' to toggle narrative linker mode
  useHotkeys('n', () => {
    const newState = !narrativeLinkerActive;
    setNarrativeLinkerActive(newState);
    // When exiting mode, clear any selected source
    if (!newState) {
      clearLinkedAppId('');
    }
  });

  // Phase 4: Quick Frame Creation (Shift+F) - Mechanism 4 enhancement
  // Creates Frame at cursor position with standard presentation size (1920×1080)
  useHotkeys('shift+f', () => {
    if (!user) return;

    // Get cursor position in board coordinates
    const cursor = getBoardCursor();

    // Count existing Frames to auto-number (use fresh data, not throttled)
    const allApps = useAppStore.getState().apps;
    const existingFrames = allApps.filter((app) => app.data.type === 'Frame');
    const slideNumber = existingFrames.length + 1;

    // Standard presentation size (1920×1080)
    const standardSize = { width: 1920, height: 1080, depth: 0 };

    // Center the Frame around cursor position
    const framePosition = {
      x: cursor.x - standardSize.width / 2,
      y: cursor.y - standardSize.height / 2,
      z: 0,
    };

    // Create Frame app
    const newFrame = {
      title: `Slide ${slideNumber}`,
      roomId: props.roomId,
      boardId: props.boardId,
      position: framePosition,
      size: standardSize,
      rotation: { x: 0, y: 0, z: 0 },
      type: 'Frame' as const,
      state: { executeInfo: { executeFunc: '', params: {} } },
      raised: false,
      dragging: false,
      pinned: false,
    };

    createApp(newFrame);

    // Show success notification
    toast({
      title: `Slide ${slideNumber} Created`,
      description: `Frame created at cursor position (${Math.round(cursor.x)}, ${Math.round(cursor.y)})`,
      status: 'success',
      duration: 2000,
      isClosable: true,
      position: 'top',
    });
  });

  // Phase 4: Create Slide Deck (Shift+D) - Mechanism 4 enhancement
  // Opens dialog to create multiple Frames in grid layout
  useHotkeys('shift+d', () => {
    if (!user) return;
    const cursor = getBoardCursor();
    setSlideDeckStartPosition({ x: cursor.x, y: cursor.y });
    setShowSlideDeckDialog(true);
  });

  /////////////////////////////////
  // UI Store Board Syncronizers //
  /////////////////////////////////
  // Forward boardPosition to localBoardPosition
  useEffect(() => {
    setLocalBoardPosition({ x: boardPosition.x, y: boardPosition.y, scale: scale });
  }, [boardPosition.x, boardPosition.y, scale]);

  // Forwards synced information to uiStore
  useEffect(() => {
    setBoardSynced(localSynced);
  }, [localSynced]);

  // Forward local position and scale to uiStore
  useEffect(() => {
    if (movementTimeoutRef.current !== null) {
      window.clearTimeout(movementTimeoutRef.current);
    }
    if (movementZoomSafetyTimeoutRef.current !== null) {
      window.clearTimeout(movementZoomSafetyTimeoutRef.current);
    }
    setLocalSynced(false);

    movementTimeoutRef.current = window.setTimeout(() => {
      setBoardPosition({ x: localBoardPosition.x, y: localBoardPosition.y });
      setScale(localBoardPosition.scale);

      // This secondary delay may not be strickly neccessary as we also have rndSafety.tsx
      movementZoomSafetyTimeoutRef.current = window.setTimeout(() => {
        setLocalSynced(true);
      }, 50);
    }, 250);
  }, [localBoardPosition.x, localBoardPosition.y, localBoardPosition.scale]);

  ////////////////////////
  // Local Calculations //
  ////////////////////////
  const localZoomInDelta = (d: number, cursor: { x: number; y: number }) => {
    const step = Math.min(Math.abs(d), 10) * WheelStepZoom;
    setLocalBoardPosition((prev) => {
      return zoomOnLocationNewPosition(
        { x: prev.x, y: prev.y },
        { x: cursor.x, y: cursor.y },
        prev.scale,
        Math.min(prev.scale + step * prev.scale, MaxZoom)
      );
    });
  };
  const localZoomOutDelta = (d: number, cursor: { x: number; y: number }) => {
    const step = Math.min(Math.abs(d), 10) * WheelStepZoom;
    setLocalBoardPosition((prev) => {
      return zoomOnLocationNewPosition(
        { x: prev.x, y: prev.y },
        { x: cursor.x, y: cursor.y },
        prev.scale,
        Math.max(prev.scale - step * prev.scale, MinZoom)
      );
    });
  };

  /////////////////////////////////
  // User Target Element Checker //
  /////////////////////////////////
  // Classifies where a mousedown landed so move handlers know whether to pan the board.
  // Runs in the capture phase (before element handlers), so it sees every click first.
  // shiftKey: when shift is held on the lasso SVG, preserve selectedAppId so Lasso.tsx
  // can seed it into the new lasso group before clearing it.
  const draggedOnCheck = (target: HTMLElement, shiftKey = false) => {
    if (target.id === 'board') {
      setStartedDragOn('board');
      setSelectedApp('');
    } else if (target.id === 'lasso' || target.id === 'whiteboard') {
      setStartedDragOn('board-actions');
      if (!shiftKey) setSelectedApp('');
    } else if (target.classList.contains('handle')) {
      setStartedDragOn('app');
    } else if (target.classList.contains('app-window-resize-handle')) {
      setStartedDragOn('app-resize');
    } else {
      setStartedDragOn('other');
    }
  };

  const draggedOnTouchCheck = (event: TouchEvent) => {
    const checkValidIds = (validIds: string[]) => {
      const allTouchesAreOnValidID = Array.from(event.touches).every((touch) => validIds.includes((touch.target as HTMLElement).id));
      return allTouchesAreOnValidID;
    };

    const checkValidClassIfOnlyOneTouch = (className: string) => {
      const allTouchesAreOnValidClass = Array.from(event.touches).some((touch) =>
        (touch.target as HTMLElement).classList.contains(className)
      );
      return allTouchesAreOnValidClass;
    };

    if (checkValidClassIfOnlyOneTouch('handle')) {
      setStartedDragOn('app');
    } else if (checkValidClassIfOnlyOneTouch('app-window-resize-handle')) {
      setStartedDragOn('app-resize');
    } else if (checkValidIds(['board'])) {
      setStartedDragOn('board');
      setSelectedApp('');
    } else if (checkValidIds(['lasso', 'whiteboard'])) {
      setStartedDragOn('board-actions');
      setSelectedApp('');
    } else {
      setStartedDragOn('other');
    }
  };

  /////////////////////////////////////////////////////////////////////////////////
  // Code to be Triggered Upon Deslecting App in Grab Mode via Movement Commands //
  /////////////////////////////////////////////////////////////////////////////////
  const grabModeDeselection = () => {
    // // Uncomment me if you want the disable app deselect on input fields
    // const target = event.target as HTMLElement;
    // const computedStyle = window.getComputedStyle(target);
    // const isTextEditable =
    //   target.tagName === 'INPUT' ||
    //   target.tagName === 'TEXTAREA' ||
    //   target.getAttribute('contenteditable') === 'true' ||
    //   computedStyle.cursor === 'text';

    // if (!isTextEditable) {
    //   setSelectedApp('');
    // }

    // Aggressive method to force deselect accidentally selection of text on screen which
    // will prevent proper dragging behaviour, uncomment code in future if needed
    if (window.getSelection) {
      window.getSelection()?.removeAllRanges();
    }

    // Deselect application when pan moves in grab mode
    // Caviat, first input will be ignored
    setSelectedApp('');

    setLocalSynced((prev) => {
      if (prev) {
        setCursorPos((cur) => {
          const element = document.elementFromPoint(cur.x, cur.y);
          draggedOnCheck(element as HTMLElement);
          return cur;
        });
      }
      return prev;
    });
  };

  ///////////////////////////////////////////
  // Mouse/TouchPad/TouchScreen Behaviours //
  ///////////////////////////////////////////
  // (INITAL CLICKS) Make sure the initial mouse click is on a valid surface
  useEffect(() => {
    const handleMouseStart = (event: MouseEvent) => {
      // Prevent dragging for everyone due to MacOS ctrl + leftclick bringing up context menu
      if (event.ctrlKey && event.buttons & 1) {
        setStartedDragOn('other');
      } else {
        draggedOnCheck(event.target as HTMLElement, event.shiftKey);
      }
    };

    const handleTouchStart = (event: TouchEvent) => {
      if (event.touches.length >= 1) {
        draggedOnTouchCheck(event);

        setLastTouch(
          Array.from(event.touches).map((touch, index) => {
            return { x: touch.clientX, y: touch.clientY };
          })
        );
      }
    };

    window.addEventListener('touchstart', handleTouchStart, { passive: false });
    window.addEventListener('mousedown', handleMouseStart, { capture: true, passive: false });
    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('mousedown', handleMouseStart);
    };
  }, []);

  // (ON MOVE) Movement
  useEffect(() => {
    // Mouse (Scroll Wheel) & Touchpad (Two Finger Pan)
    const handleMove = (event: WheelEvent) => {
      if (event.ctrlKey) {
        event.preventDefault(); // Page Zoom Inhibitors
      }

      if (boardLocked) {
        return;
      }

      // This is a workable solution to have a psudeo onWheelStart-like behaviour
      // Note that if someone is wheeling on the board and then quickly wheels on a panel, the board will move
      // until the user stops giving input and then the proper behaviour will resume
      setLocalSynced((prev) => {
        if (prev) {
          draggedOnCheck(event.target as HTMLElement);
        }
        return prev;
      });

      if (selectedApp) {
        // if (primaryActionMode === 'grab') {
        //   grabModeDeselection();
        // }
        return;
      }

      setStartedDragOn((draggedOn) => {
        if (draggedOn === 'other') {
          return draggedOn;
        }
        // Zooming
        if (event.ctrlKey || event.metaKey) {
          const cursor = { x: event.clientX, y: event.clientY };
          if (event.deltaY < 0) {
            localZoomInDelta(event.deltaY, cursor);
          } else if (event.deltaY > 0) {
            localZoomOutDelta(event.deltaY, cursor);
          }
        }
        // Transversal/Panning
        else {
          // Flip axis for mouse scroll wheel users
          setLocalBoardPosition((prev) => ({
            x: prev.x - (!isMac && event.shiftKey ? event.deltaY : event.deltaX) / prev.scale,
            y: prev.y - (!isMac && event.shiftKey ? event.deltaX : event.deltaY) / prev.scale,
            scale: prev.scale,
          }));
        }
        return draggedOn;
      });
    };

    // Mouse (Left Click)
    const handleMouseMove = (event: MouseEvent) => {
      if (boardLocked) {
        return;
      }

      if (selectedApp) {
        // if (primaryActionMode === 'grab' && (event.buttons & 1 || event.buttons & 4)) {
        //   grabModeDeselection();
        // }
        return;
      }

      const move = () => {
        setLocalBoardPosition((prev) => ({
          x: prev.x + (event.movementX * 1) / prev.scale,
          y: prev.y + (event.movementY * 1) / prev.scale,
          scale: prev.scale,
        }));

        event.stopPropagation();
        event.preventDefault();
      };

      setStartedDragOn((draggedOn) => {
        // Tranversal/Panning
        if (primaryActionMode === 'grab' && event.buttons & 1 && draggedOn !== 'other') {
          move();
        } else if (primaryActionMode === 'linker' && event.buttons & 1 && draggedOn !== 'other' && draggedOn !== 'app') {
          move();
        } else if (event.buttons & 4 && (draggedOn === 'app' || draggedOn === 'board' || draggedOn === 'board-actions')) {
          move();
        }
        return draggedOn;
      });
    };

    // Touch Screen
    const handleTouchMove = (event: TouchEvent) => {
      event.preventDefault(); // Page Zoom Inhibitors
      event.stopPropagation();

      if (boardLocked) {
        return;
      }

      if (selectedApp) {
        // if (primaryActionMode === 'grab') {
        //   grabModeDeselection();
        // }
        return;
      }

      setStartedDragOn((draggedOn) => {
        if (draggedOn === 'other') {
          return draggedOn;
        }
        if (event.touches.length === 1) {
          // Looking for lasso interaction? Touch lasso are handled in Lasso.tsx
          // Looking for linker interaction? linker are handled in AppWindow.tsx
          if (
            (primaryActionMode === 'grab' && draggedOn !== 'board-actions') ||
            (primaryActionMode === 'linker' && draggedOn !== 'board-actions' && draggedOn !== 'app')
          ) {
            setLastTouch((prev) => {
              if (prev.length !== 1) {
                return prev;
              }

              const delta0X = prev[0].x - event.touches[0].clientX;
              const delta0Y = prev[0].y - event.touches[0].clientY;

              setLocalBoardPosition((prevBoard) => {
                return {
                  x: prevBoard.x - delta0X / prevBoard.scale,
                  y: prevBoard.y - delta0Y / prevBoard.scale,
                  scale: prevBoard.scale,
                };
              });
              // }
              return [{ x: event.touches[0].clientX, y: event.touches[0].clientY }];
            });
          }
        } else if (
          // If in grab mode, allow 2 finger gesture on all but 'other'; if not in grab, limit to not allow drag on app or app-resize
          event.touches.length === 2 &&
          ((primaryActionMode !== 'grab' && draggedOn !== 'app' && draggedOn !== 'app-resize') || primaryActionMode === 'grab')
        ) {
          setLastTouch((prev) => {
            if (prev.length < 2) {
              return prev;
            }

            const delta0X = prev[0].x - event.touches[0].clientX;
            const delta0Y = prev[0].y - event.touches[0].clientY;

            const delta1X = prev[1].x - event.touches[1].clientX;
            const delta1Y = prev[1].y - event.touches[1].clientY;

            // Pan
            const avgDeltaX = (delta0X + delta1X) / 2;
            const avgDeltaY = (delta0Y + delta1Y) / 2;

            // Zoom
            const prevDistance = magnitude({ x: prev[0].x, y: prev[0].y }, { x: prev[1].x, y: prev[1].y });
            const distance = magnitude(
              { x: event.touches[0].clientX, y: event.touches[0].clientY },
              { x: event.touches[1].clientX, y: event.touches[1].clientY }
            );
            const zoomDeltaRaw = prevDistance - distance;
            const avgX = (event.touches[0].clientX + event.touches[1].clientX) / 2;
            const avgY = (event.touches[0].clientY + event.touches[1].clientY) / 2;

            if (prevDistance > 0 && Math.abs(zoomDeltaRaw) > TOUCH_PINCH_DEADZONE) {
              // Use ratio-based zoom for smooth, continuous pinch behaviour.
              // This avoids the discrete "stepping" of the wheel-based zoom
              // helpers which quantize through WheelStepZoom.
              const rawRatio = distance / prevDistance;
              // Clamp the per-frame ratio so zoom can't jump too fast
              const ratio = Math.max(TOUCH_PINCH_MIN_RATIO, Math.min(rawRatio, TOUCH_PINCH_MAX_RATIO));

              setLocalBoardPosition((prevBoard) => {
                const newScale = Math.max(MinZoom, Math.min(prevBoard.scale * ratio, MaxZoom));
                return zoomOnLocationNewPosition(
                  { x: prevBoard.x, y: prevBoard.y },
                  { x: avgX, y: avgY },
                  prevBoard.scale,
                  newScale,
                );
              });
            }

            setLocalBoardPosition((prevBoard) => {
              return {
                x: prevBoard.x - avgDeltaX / prevBoard.scale,
                y: prevBoard.y - avgDeltaY / prevBoard.scale,
                scale: prevBoard.scale,
              };
            });

            return [
              { x: event.touches[0].clientX, y: event.touches[0].clientY },
              { x: event.touches[1].clientX, y: event.touches[1].clientY },
            ];
          });
        }
        return draggedOn;
      });
    };

    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('mousemove', handleMouseMove, { passive: false });
    window.addEventListener('wheel', handleMove, { passive: false });

    return () => {
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('wheel', handleMove);
    };
  }, [selectedApp, primaryActionMode, boardLocked]);

  // Arrow key panning for board navigation (outside presentation mode)
  useEffect(() => {
    const handleArrowKeys = (event: KeyboardEvent) => {
      // Skip if in presentation mode (let PresentationController handle arrow keys)
      const uiState = useUIStore.getState();
      if (uiState.presentationMode) return;

      // Skip if user is typing in an input field
      const target = event.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.getAttribute('contenteditable') === 'true'
      ) {
        return;
      }

      // Pan amount: 100 board pixels (adjusts with zoom)
      const panAmount = 100;
      let handled = false;

      // Arrow key panning: Move viewport in the direction of the arrow
      // (not drag-paper metaphor - this is camera/viewport movement)
      if (event.key === 'ArrowUp') {
        setLocalBoardPosition((prev) => ({
          x: prev.x,
          y: prev.y - panAmount / prev.scale, // Move viewport up
          scale: prev.scale,
        }));
        handled = true;
      } else if (event.key === 'ArrowDown') {
        setLocalBoardPosition((prev) => ({
          x: prev.x,
          y: prev.y + panAmount / prev.scale, // Move viewport down
          scale: prev.scale,
        }));
        handled = true;
      } else if (event.key === 'ArrowLeft') {
        setLocalBoardPosition((prev) => ({
          x: prev.x - panAmount / prev.scale, // Move viewport left
          y: prev.y,
          scale: prev.scale,
        }));
        handled = true;
      } else if (event.key === 'ArrowRight') {
        setLocalBoardPosition((prev) => ({
          x: prev.x + panAmount / prev.scale, // Move viewport right
          y: prev.y,
          scale: prev.scale,
        }));
        handled = true;
      }

      if (handled) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    window.addEventListener('keydown', handleArrowKeys);
    return () => window.removeEventListener('keydown', handleArrowKeys);
  }, []);

  // Helper function to render board content at a specific viewport
  const renderBoardContent = (position: { x: number; y: number }, boardScale: number) => (
    <Box transform={`scale(${boardScale})`} transformOrigin={'top left'}>
      <div
        style={{
          position: 'absolute',
          left: position.x,
          top: position.y,
          width: boardWidth,
          height: boardHeight,
        }}
      >
        {primaryActionMode === 'linker' && <LinkerMode />}

        {/* Temporary: NarrativeLinkerMode for testing (Phase 1A.3) - Press 'N' key to toggle */}
        {narrativeLinkerActive && (
          <NarrativeLinkerMode
            roomId={props.roomId}
            boardId={props.boardId}
            onExit={() => {
              setNarrativeLinkerActive(false);
              clearLinkedAppId('');
            }}
          />
        )}

        {/* The board's apps */}
        <Apps />

        {/* Whiteboard */}
        <WhiteboardMemo roomId={props.roomId} boardId={props.boardId} />

        {/* Lasso */}
        {canLasso && primaryActionMode === 'lasso' && <LassoMemo roomId={props.roomId} boardId={props.boardId} />}

        <Links />
        <CursorArrow />

        {/* Presence of the users */}
        <PresenceComponent boardId={props.boardId} />

        {/* Draggable Background */}
        <Background boardId={props.boardId} roomId={props.roomId} />
      </div>
    </Box>
  );

  // Phase 4B: Sidebar Anchors - Check if sidebar should be shown
  // Show sidebar during presentation if there are anchored apps
  // Both presenter and wall see the sidebar (WYSIWYG)
  const isPresentationActive = presences.some((p) => p.data.presentationMode === true);
  const presenterPresence = presences.find((p) => p.data.presentationMode === true);
  const anchoredAppIds = presenterPresence?.data.anchoredAppIds || [];
  const shouldShowSidebar = isPresentationActive && anchoredAppIds.length > 0;

  // Sidebar rendering (Phase 4B)
  if (shouldShowSidebar) {
    const sidebarWidthPercent = 15;
    const mainContentWidthPercent = 100 - sidebarWidthPercent;

    return (
      <>
        <div style={{ display: 'flex', width: '100%', height: '100vh', overflow: 'hidden', position: 'relative' }}>
          {/* Sidebar: Anchored apps (15%) */}
          <div
            style={{
              flex: `0 0 ${sidebarWidthPercent}%`,
              height: '100%',
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            <SidebarAnchors
              anchoredAppIds={anchoredAppIds}
              sidebarWidthPercent={sidebarWidthPercent}
              position="left"
            />
          </div>

          {/* Main content: Board (85%) */}
          <div
            style={{
              flex: `0 0 ${mainContentWidthPercent}%`,
              height: '100%',
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            {renderBoardContent(localBoardPosition, localBoardPosition.scale)}
          </div>
        </div>

        {/* Phase 4: Create Slide Deck Dialog */}
        <CreateSlideDeckDialog
          isOpen={showSlideDeckDialog}
          onClose={() => setShowSlideDeckDialog(false)}
          roomId={props.roomId}
          boardId={props.boardId}
          startPosition={slideDeckStartPosition}
        />
      </>
    );
  }

  // Three-section split rendering (Phase 4)
  if (shouldSplit) {
    // Wait for apps to load before attempting split
    if (apps.length === 0) {
      // Apps not loaded yet, render single viewport temporarily
      return renderBoardContent(localBoardPosition, localBoardPosition.scale);
    }

    // Get Frame apps
    const leftFrame = getFrameById(splitConfig.leftFrameId, apps);
    const rightFrame = getFrameById(splitConfig.rightFrameId, apps);

    // Validate frames exist
    if (!leftFrame || !rightFrame) {
      // Frames deleted or invalid - fall back to normal mode
      return renderBoardContent(localBoardPosition, localBoardPosition.scale);
    }

    // Calculate section widths
    const totalWidth = window.innerWidth;
    const totalHeight = window.innerHeight;
    const leftWidth = (splitConfig.leftWidth / 100) * totalWidth;
    const middleWidth = (splitConfig.middleWidth / 100) * totalWidth;
    const rightWidth = totalWidth - leftWidth - middleWidth;

    // Get viewport for left section (Frame-based, static)
    const leftViewport = getFrameViewport(leftFrame, leftWidth, totalHeight);

    // Get viewport for middle section (follows presenter)
    let middleViewport = { x: localBoardPosition.x, y: localBoardPosition.y, scale: localBoardPosition.scale };
    if (following) {
      // If following someone, use their viewport
      const followedPresence = presences.find((p) => p._id === following);
      if (followedPresence) {
        // Transform presence viewport to camera coordinates
        const { position, size } = followedPresence.data.viewport;
        const vx = -position.x;
        const vy = -position.y;
        const vw = -size.width;
        const vh = -size.height;
        const vcx = vx + vw / 2;
        const vcy = vy + vh / 2;
        const s = Math.min(middleWidth / -vw, totalHeight / -vh);
        const cx = vcx + middleWidth / s / 2;
        const cy = vcy + totalHeight / s / 2;
        middleViewport = { x: cx, y: cy, scale: s };
      }
    }

    // Get viewport for right section (Frame-based, static)
    const rightViewport = getFrameViewport(rightFrame, rightWidth, totalHeight);

    // Render three sections
    return (
      <>
        <div style={{ display: 'flex', width: '100%', height: '100vh', overflow: 'hidden' }}>
          {/* LEFT: Static reference content (Frame) */}
          <div
            style={{
              flex: `0 0 ${leftWidth}px`,
              height: '100%',
              borderRight: `4px solid ${dividerColor}`,
              overflow: 'hidden',
              background: sectionBg,
              position: 'relative',
            }}
          >
            {renderBoardContent(leftViewport, leftViewport.scale)}
          </div>

          {/* MIDDLE: Dynamic content (follows presenter) */}
          <div
            style={{
              flex: `0 0 ${middleWidth}px`,
              height: '100%',
              borderRight: `4px solid ${dividerColor}`,
              overflow: 'hidden',
              background: middleBg,
              position: 'relative',
            }}
          >
            {renderBoardContent(middleViewport, middleViewport.scale)}
          </div>

          {/* RIGHT: Static reference content (Frame) */}
          <div
            style={{
              flex: `0 0 ${rightWidth}px`,
              height: '100%',
              overflow: 'hidden',
              background: sectionBg,
              position: 'relative',
            }}
          >
            {renderBoardContent(rightViewport, rightViewport.scale)}
          </div>
        </div>

        {/* Phase 4: Create Slide Deck Dialog */}
        <CreateSlideDeckDialog
          isOpen={showSlideDeckDialog}
          onClose={() => setShowSlideDeckDialog(false)}
          roomId={props.roomId}
          boardId={props.boardId}
          startPosition={slideDeckStartPosition}
        />
      </>
    );
  }

  // Normal mode: Single viewport (for presenter and non-wall clients)
  return (
    <>
      {renderBoardContent(localBoardPosition, localBoardPosition.scale)}

      {/* Phase 4: Create Slide Deck Dialog */}
      <CreateSlideDeckDialog
        isOpen={showSlideDeckDialog}
        onClose={() => setShowSlideDeckDialog(false)}
        roomId={props.roomId}
        boardId={props.boardId}
        startPosition={slideDeckStartPosition}
      />
    </>
  );
}
const LassoMemo = React.memo(Lasso);
const WhiteboardMemo = React.memo(Whiteboard);

// This code has been modified from the one present in useUIStore
function zoomOnLocationNewPosition(
  fromPosition: { x: number; y: number },
  towardsPos: { x: number; y: number },
  prevScale: number,
  scale: number
): { x: number; y: number; scale: number } {
  const x1 = fromPosition.x - towardsPos.x / prevScale;
  const y1 = fromPosition.y - towardsPos.y / prevScale;
  const x2 = fromPosition.x - towardsPos.x / scale;
  const y2 = fromPosition.y - towardsPos.y / scale;
  const dx = x2 - x1;
  const dy = y2 - y1;
  const x = fromPosition.x - dx;
  const y = fromPosition.y - dy;
  return { x, y, scale };
}

function magnitude(a: { x: number; y: number }, b: { x: number; y: number }) {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return Math.sqrt(dx * dx + dy * dy);
}
